/* =========================================================
   DIEZ DIGITAL DESIGN SYSTEM / CORE JS
   ========================================================= */

// 1. SCROLL REVEAL ANIMATIONS (Intersection Observer)
class DiezScrollObserver {
  constructor() {
    this.elements = document.querySelectorAll('.diez-reveal-up, .diez-fade-in');
    if (this.elements.length === 0) return;
    this.init();
  }
  init() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          // Sadece bir kere calissin, performans korunsun
          this.observer.unobserve(entry.target);
        }
      });
    }, {
      root: null,
      rootMargin: '0px 0px -10% 0px', // Ekrana girmeden hemen once tetikle
      threshold: 0.1
    });
    this.elements.forEach(el => this.observer.observe(el));
  }
}

// 2. MOBILE MENU & ACCORDION
class DiezMobileMenu {
  constructor() {
    this.buttons = document.querySelectorAll("[data-menu-toggle]");
    this.menu = document.querySelector("[data-mobile-menu]");
    this.accordions = document.querySelectorAll(".diez-mobile-accordion");
    if (!this.buttons.length || !this.menu) return;
    this.init();
  }

  init() {
    // Menu Ac/Kapa
    this.buttons.forEach(btn => {
      btn.addEventListener("click", () => {
        const isOpen = this.menu.classList.toggle("is-open");
        document.body.style.overflow = isOpen ? "hidden" : "";
      });
    });

    // Sayfa degistiginde menuyu kapat
    this.menu.querySelectorAll("a").forEach((link) => {
      link.addEventListener("click", () => {
        this.close();
      });
    });

    // Accordion Mimarisi (Erkek -> T-shirt acilma mantigi)
    this.accordions.forEach(acc => {
      acc.addEventListener("click", (e) => {
        const expanded = acc.getAttribute("aria-expanded") === "true";
        const content = acc.nextElementSibling;
        
        acc.setAttribute("aria-expanded", !expanded);
        content.style.display = expanded ? "none" : "flex";
      });
    });
  }

  close() {
    this.menu.classList.remove("is-open");
    document.body.style.overflow = "";
  }
}


// =========================================================
// DIEZ / AJAX CART HELPERS
// Cart drawer + cart count sayfa yenilenmeden güncellenir.
// =========================================================

async function diezGetCartState() {
  const response = await fetch("/cart.js", {
    method: "GET",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest"
    },
    cache: "no-store"
  });

  if (!response.ok) {
    throw new Error("Cart state could not be loaded.");
  }

  const cart = await response.json();

  document.querySelectorAll("[data-cart-count]").forEach((element) => {
    element.textContent = cart.item_count;
  });

  document.dispatchEvent(
    new CustomEvent("diez:cart-updated", {
      detail: cart
    })
  );

  return cart;
}

async function diezRefreshCartDrawerMarkup() {
  const currentDrawer = document.querySelector("[data-cart-drawer]");
  if (!currentDrawer) return null;

  /*
   * Cart drawer bir snippet oldugu icin Shopify Section API'ye bagimli kalmiyoruz.
   * Ayni sayfanin HTML'ini yeni cart cookie'siyle tekrar alip sadece drawer icini yeniliyoruz.
   */
  const response = await fetch(window.location.href, {
    method: "GET",
    headers: {
      Accept: "text/html",
      "X-Requested-With": "XMLHttpRequest"
    },
    cache: "no-store",
    credentials: "same-origin"
  });

  if (!response.ok) {
    throw new Error("Cart drawer markup could not be refreshed.");
  }

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, "text/html");
  const freshDrawer = doc.querySelector("[data-cart-drawer]");

  if (!freshDrawer) {
    throw new Error("Fresh cart drawer markup was not found.");
  }

  // Root node ayni kalir; document event delegation bozulmaz.
  currentDrawer.innerHTML = freshDrawer.innerHTML;

  return currentDrawer;
}

function diezOpenCartDrawer() {
  const drawer = document.querySelector("[data-cart-drawer]");
  if (!drawer) return;

  drawer.classList.add("is-open");
  drawer.setAttribute("aria-hidden", "false");
  document.body.style.overflow = "hidden";
}

function diezCloseCartDrawer() {
  const drawer = document.querySelector("[data-cart-drawer]");
  if (!drawer) return;

  drawer.classList.remove("is-open");
  drawer.setAttribute("aria-hidden", "true");
  document.body.style.overflow = "";
}


// 3. CART DRAWER
class DiezCartDrawer {
  constructor() {
    this.drawer = document.querySelector("[data-cart-drawer]");
    if (!this.drawer) return;
    this.init();
  }

  init() {
    document.addEventListener("click", (event) => {
      if (event.target.closest("[data-cart-close]")) {
        event.preventDefault();
        diezCloseCartDrawer();
        return;
      }

      if (event.target.closest("[data-open-cart]")) {
        event.preventDefault();
        diezOpenCartDrawer();
        return;
      }

      const changeButton = event.target.closest("[data-cart-change]");

      if (changeButton) {
        event.preventDefault();

        this.changeQuantity(
          changeButton.dataset.cartChange,
          Number(changeButton.dataset.quantity),
          changeButton
        );
      }
    });
  }

  async changeQuantity(key, quantity, button) {
    if (!key) return;

    if (button) {
      button.disabled = true;
      button.setAttribute("aria-busy", "true");
    }

    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          id: key,
          quantity: Math.max(quantity, 0)
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(
          error.description ||
          error.message ||
          "Cart update failed."
        );
      }

      await diezGetCartState();
      await diezRefreshCartDrawerMarkup();

      // + / - sonrasinda drawer acik kalsin.
      diezOpenCartDrawer();

    } catch (error) {
      console.error("DIEZ cart quantity error:", error);

      if (button) {
        button.disabled = false;
        button.removeAttribute("aria-busy");
      }
    }
  }
}

// 4. PRODUCT VARIANTS
class DiezProductVariants {
  constructor() {
    this.form = document.querySelector("#diez-product-form");
    this.productJson = document.querySelector("[data-product-json]");
    this.productRoot = document.querySelector("[data-diez-product]");

    if (!this.form || !this.productJson) return;

    this.product = JSON.parse(this.productJson.textContent);
    this.variantInput = this.form.querySelector("[data-product-variant-id]");
    this.submitButton = this.form.querySelector("[data-product-submit]") || this.form.querySelector('[type="submit"]');
    this.stickyButton = document.querySelector("[data-sticky-add]");
    this.optionInputs = [...this.form.querySelectorAll("[data-option-index]")];
    this.sizeInputs = [...this.form.querySelectorAll("[data-size-option='true']")];
    this.preorderActive = this.productRoot?.dataset.preorderActive === "true";

    this.init();
  }

  init() {
    this.optionInputs.forEach((input) => {
      input.addEventListener("change", () => this.updateVariant());
    });

    this.updateVariant();
  }

  getSelectedOptions() {
    return this.product.options.map((_, index) => {
      return this.form.querySelector(`[data-option-index="${index}"]:checked`)?.value || null;
    });
  }

  setButtonState({ disabled, text, stickyText }) {
    if (this.submitButton) {
      this.submitButton.disabled = disabled;
      this.submitButton.textContent = text;
    }

    if (this.stickyButton) {
      this.stickyButton.disabled = disabled;
      this.stickyButton.textContent = stickyText || text;
    }
  }

  updateVariant() {
    const selectedOptions = this.getSelectedOptions();
    const missingOption = selectedOptions.some(option => !option);

    if (missingOption) {
      this.variantInput.value = "";

      const sizeMissing =
        this.sizeInputs.length > 0 &&
        !this.sizeInputs.some(input => input.checked);

      this.setButtonState({
        disabled: true,
        text: sizeMissing ? "ÖNCE BEDEN SEÇ" : "SEÇİMİNİ TAMAMLA",
        stickyText: sizeMissing ? "BEDEN SEÇ" : "SEÇİM YAP"
      });

      return;
    }

    const variant = this.product.variants.find((item) => {
      return item.options.every((option, index) => option === selectedOptions[index]);
    });

    if (!variant) {
      this.variantInput.value = "";
      this.setButtonState({
        disabled: true,
        text: "BU SEÇENEK YOK",
        stickyText: "MEVCUT DEĞİL"
      });
      return;
    }

    this.variantInput.value = variant.id;

    if (this.preorderActive) {
      this.setButtonState({
        disabled: false,
        text: "ÖN SİPARİŞ VER →",
        stickyText: "ÖN SİPARİŞ"
      });
    } else {
      const available = Boolean(variant.available);

      this.setButtonState({
        disabled: !available,
        text: available ? "SEPETE AT →" : "TÜKENDİ",
        stickyText: available ? "SEPETE AT" : "TÜKENDİ"
      });
    }

    document.querySelectorAll("[data-product-price]").forEach((element) => {
      element.textContent = this.formatMoney(variant.price);
    });

    document.dispatchEvent(
      new CustomEvent("diez:variant-changed", {
        detail: { variant, selectedOptions }
      })
    );
  }

  formatMoney(cents) {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }
}

class DiezProductExtras {
  constructor() {
    this.drawer = document.querySelector("[data-size-guide]");
    this.copyButtons = document.querySelectorAll("[data-copy-code]");
    this.init();
  }

  init() {
    document.addEventListener("click", (event) => {
      if (event.target.closest("[data-size-guide-open]")) {
        event.preventDefault();
        this.openDrawer();
      }

      if (event.target.closest("[data-size-guide-close]")) {
        event.preventDefault();
        this.closeDrawer();
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") this.closeDrawer();
    });

    this.copyButtons.forEach((button) => {
      button.addEventListener("click", async () => {
        const code = button.dataset.copyCode;
        if (!code) return;

        try {
          await navigator.clipboard.writeText(code);
          button.classList.add("is-copied");

          const label = button.querySelector("[data-copy-label]");
          if (label) label.textContent = "KOPYALANDI";

          window.setTimeout(() => {
            button.classList.remove("is-copied");
            if (label) label.textContent = "KOPYALA";
          }, 1400);
        } catch (error) {
          console.warn("DIEZ discount code copy failed.", error);
        }
      });
    });
  }

  openDrawer() {
    if (!this.drawer) return;
    this.drawer.classList.add("is-open");
    this.drawer.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("diez-size-lock");
  }

  closeDrawer() {
    if (!this.drawer) return;
    this.drawer.classList.remove("is-open");
    this.drawer.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("diez-size-lock");
  }
}

// 5. AJAX CART
class DiezAjaxCart {
  constructor() {
    this.form = document.querySelector("#diez-product-form");

    if (!this.form) return;

    this.init();
  }

  init() {
    this.form.addEventListener("submit", async (event) => {
      /*
       * Kritik nokta:
       * Shopify'in normal form submit'ini tamamen durduruyoruz.
       * Boylece sayfa HIC yenilenmiyor.
       */
      event.preventDefault();
      event.stopPropagation();

      const submitButton =
        this.form.querySelector("[data-product-submit]") ||
        this.form.querySelector('[type="submit"]');

      if (!submitButton || submitButton.disabled) return;

      const variantInput = this.form.querySelector("[data-product-variant-id]");

      if (!variantInput || !variantInput.value) {
        submitButton.disabled = true;
        submitButton.textContent = "ÖNCE BEDEN SEÇ";
        return;
      }

      const originalText = submitButton.textContent.trim();

      const preorderActive =
        document.querySelector("[data-diez-product]")?.dataset.preorderActive === "true";

      submitButton.disabled = true;
      submitButton.setAttribute("aria-busy", "true");

      submitButton.textContent = preorderActive
        ? "ÖN SİPARİŞ EKLENİYOR..."
        : "SEPETE EKLENİYOR...";

      try {
        const formData = new FormData(this.form);

        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          body: formData
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));

          throw new Error(
            error.description ||
            error.message ||
            "Ürün sepete eklenemedi."
          );
        }

        /*
         * Sira önemli:
         * 1. cart count / state
         * 2. drawer HTML
         * 3. drawer aç
         */
        await diezGetCartState();
        await diezRefreshCartDrawerMarkup();
        diezOpenCartDrawer();

        submitButton.textContent = preorderActive
          ? "ÖN SİPARİŞ EKLENDİ ✓"
          : "SEPETE EKLENDİ ✓";

        window.setTimeout(() => {
          submitButton.textContent = originalText;
          submitButton.disabled = false;
          submitButton.removeAttribute("aria-busy");
        }, 850);

      } catch (error) {
        console.error("DIEZ add-to-cart error:", error);

        submitButton.textContent = "TEKRAR DENE";

        window.setTimeout(() => {
          submitButton.textContent = originalText;
          submitButton.disabled = false;
          submitButton.removeAttribute("aria-busy");
        }, 1200);
      }
    });
  }
}

// 6. GLOBAL INITIALIZATION
document.addEventListener("DOMContentLoaded", () => {
  // Initialize Architecture
  new DiezScrollObserver();
  new DiezMobileMenu();
  new DiezCartDrawer();
  new DiezProductVariants();
  new DiezProductExtras();
  new DiezAjaxCart();

  // Sticky Cart Logic
  const stickyButton = document.querySelector("[data-sticky-add]");
  const productForm = document.querySelector("#diez-product-form");
  
  if (stickyButton && productForm) {
    stickyButton.addEventListener("click", () => {
      const submitButton =
        productForm.querySelector("[data-product-submit]") ||
        productForm.querySelector('[type="submit"]');

      if (!submitButton || submitButton.disabled) return;

      /*
       * requestSubmit() form'un submit event'ini tetikler.
       * Böylece mobil sticky buton da AJAX akisini kullanir.
       */
      if (typeof productForm.requestSubmit === "function") {
        productForm.requestSubmit(submitButton);
      } else {
        submitButton.click();
      }
    });
  }
});